export type PersistentStorageStatus = 'granted' | 'denied' | 'unsupported' | 'error'

export interface PersistentStorageResult {
    status: PersistentStorageStatus
    error?: unknown
}

interface StorageManagerLike {
    persisted?: () => Promise<boolean>
    persist?: () => Promise<boolean>
}

interface WindowWithStorage {
    navigator?: {
        storage?: StorageManagerLike
    }
}

const attempts = new WeakMap<object, Promise<PersistentStorageResult>>()

/**
 * Best-effort protection against browser eviction of the local wallet database.
 * This does not back up wallet data and callers should not make wallet access
 * conditional on a positive result.
 */
export function requestPersistentStorage(
    browserWindow: WindowWithStorage | undefined = typeof window === 'undefined'
        ? undefined
        : window,
): Promise<PersistentStorageResult> {
    if (!browserWindow) {
        return Promise.resolve({ status: 'unsupported' })
    }

    const existingAttempt = attempts.get(browserWindow)
    if (existingAttempt) {
        return existingAttempt
    }

    const storage = browserWindow.navigator?.storage
    const attempt = (async (): Promise<PersistentStorageResult> => {
        if (!storage?.persisted || !storage.persist) {
            return { status: 'unsupported' }
        }

        try {
            if (await storage.persisted()) {
                return { status: 'granted' }
            }

            return (await storage.persist()) ? { status: 'granted' } : { status: 'denied' }
        } catch (error) {
            return { status: 'error', error }
        }
    })()

    attempts.set(browserWindow, attempt)
    return attempt
}
